import PropTypes from "prop-types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../parts/modal-new";
import {
  defaultStep,
  insertStep,
  toApiSteps,
  updateStepConfig,
} from "../../../../utils/automation-builder";
import ProductChip from "../automations/ProductChip";
import FilterChips, { valueNames } from "../builder/FilterChips";
import { saveChanges } from "../builder/save-changes";

const id = "modal-automation-filter";
const SOURCES = {
  membershipTypes: "membershipTypes",
  locations: "locations",
  coaches: "coaches",
};

// The values a filter field can take: the account's own, or the fixed ones of the field
const BILLING_ORDER = ["annual", "monthly", "weekly"];

export const optionsOf = (field, options) => {
  if (field.optionsSource) return options?.[SOURCES[field.optionsSource]] ?? [];
  let values = field.options ?? [];
  if (field.type === "boolean") values = [true, false];
  if (field.key === "billingFrequency") {
    values = [...values].sort(
      (a, b) => BILLING_ORDER.indexOf(a) - BILLING_ORDER.indexOf(b)
    );
  }
  return values.map((value) => ({ id: value, name: String(value) }));
};

const FieldSection = ({ field, options, values, onChange }) => {
  const { t } = useTranslation("coaching-portal");
  const tr = (key) => t(`emailAutomations.builder.${key}`);
  const choices = optionsOf(field, options);
  const names = new Map(
    choices.map((choice, index) => [
      choice.id,
      valueNames(field.key, [choice.id], options, t)[0] ?? choices[index].name,
    ])
  );
  const title = t(`emailAutomations.builder.filterModal.titles.${field.key}`, {
    defaultValue: tr(`filter.fields.${field.key}`),
  });
  const onlyOne = field.optionsSource === "locations" && choices.length === 1;
  const toggle = (value) =>
    onChange(
      values.includes(value)
        ? values.filter((item) => item !== value)
        : [...values, value]
    );

  return (
    <fieldset className="ab-field">
      <legend>{title}</legend>
      <div className="ab-field__options">
        {onlyOne ? (
          <label className="ab-check ab-check--locked">
            <input
              type="checkbox"
              className="sr-only"
              checked
              disabled
              readOnly
            />
            <i aria-hidden="true" />
            <span>{choices[0].name}</span>
            <small>{tr("filterModal.onlyLocation")}</small>
          </label>
        ) : (
          <>
            <label className="ab-check">
              <input
                type="checkbox"
                className="sr-only"
                checked={values.length === 0}
                onChange={() => onChange([])}
              />
              <i aria-hidden="true" />
              <span>{tr("filterModal.any")}</span>
            </label>
            {choices.map((choice) => (
              <label key={choice.id} className="ab-check">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={values.includes(choice.id)}
                  onChange={() => toggle(choice.id)}
                />
                <i aria-hidden="true" />
                <span>
                  {field.key === "userType"
                    ? tr(`filterModal.userTypes.${choice.id}`)
                    : (names.get(choice.id) ?? choice.name)}
                </span>
              </label>
            ))}
          </>
        )}
      </div>
    </fieldset>
  );
};

FieldSection.propTypes = {
  field: PropTypes.object.isRequired,
  options: PropTypes.object,
  values: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

/**
 * The filter of an automation: its trigger's own filter, a filter step being edited, or a new one.
 * `target` is "trigger", "step:<index>" or "new:<slot>".
 */
const AutomationFilter = ({ detail, definition, options, target }) => {
  const { t } = useTranslation("coaching-portal");
  const tr = (key, params) =>
    t(`emailAutomations.builder.filterModal.${key}`, params);
  const { automation, steps } = detail;
  const apiSteps = toApiSteps(steps);
  const [kind, rawIndex] = target.split(":");
  const index = Number(rawIndex);

  let initial = {};
  if (kind === "trigger") {
    initial = automation.filters;
  } else if (kind === "step") {
    initial = apiSteps[index]?.config.filters;
  }
  const [filters, setFilters] = useState(initial ?? {});
  const fields = definition.filterFields;

  const setField = (key, values) =>
    setFilters((current) => {
      const next = { ...current, [key]: values };
      if (values.length === 0) delete next[key];
      return next;
    });

  const save = (event) => {
    event.preventDefault();
    if (kind === "trigger") {
      saveChanges(automation._id, { filters });
    } else if (kind === "step") {
      saveChanges(automation._id, {
        steps: updateStepConfig(apiSteps, index, { filters }),
      });
    } else {
      const step = { ...defaultStep("filter"), config: { filters } };
      saveChanges(automation._id, { steps: insertStep(apiSteps, index, step) });
    }
  };
  const editing = kind !== "new";

  return (
    <form onSubmit={save}>
      <Modal
        name={id}
        key={id}
        className="ea-modal ea-modal--wide"
        customTitle={
          <h2 className="ea-modal__title">
            {tr(editing ? "edit" : "add")}
            <ProductChip product={automation.product} />
          </h2>
        }
      >
        <div className="body">
          {fields.map((field) => (
            <FieldSection
              key={field.key}
              field={field}
              options={options}
              values={filters[field.key] ?? []}
              onChange={(values) => setField(field.key, values)}
            />
          ))}
          <section className="ab-summary">
            <h4>{tr("summary")}</h4>
            <div className="ab-card ab-card--filter">
              <span className="ab-card__label">
                {t("emailAutomations.builder.filter.label")}
              </span>
              <FilterChips
                filters={filters}
                fields={fields.map((field) => field.key)}
                options={options}
              />
            </div>
          </section>
        </div>
        <footer className="foot">
          {/* biome-ignore lint/a11y/useSemanticElements: a real link so Rill navigates; role only picks up button styles */}
          <a //NOSONAR
            href="/actions/refresh?modal=&automation=&target="
            role="button"
            data-button-type="secondary"
            data-action="cancel"
          >
            {tr("cancel")}
          </a>
          <button type="submit" data-button-type="primary">
            {tr(editing ? "saveButton" : "addButton")}
          </button>
        </footer>
      </Modal>
    </form>
  );
};

AutomationFilter.propTypes = {
  detail: PropTypes.object.isRequired,
  definition: PropTypes.object.isRequired,
  options: PropTypes.object,
  target: PropTypes.string.isRequired,
};

export default AutomationFilter;
